import { useCallback } from "react";

import {
  useReplaceEntityMutation,
  useUpdatePersonDuplicateCheckDoneMutation,
} from "src/core/gql/graphql";

type ReplaceEntityParams = {
  idToDelete: string;
  replacementId: string;
};

type MarkDuplicateCheckDoneParams = {
  personId: string;
  duplicateCheckDone?: "true" | "false";
};

/*
 * Hook dédié aux actions API utilisées pour la gestion des doublons de stakeholders.
 */
export const useStakeholderDuplicatesActions = () => {
  const [replaceEntityMutation] = useReplaceEntityMutation();
  const [updatePersonDuplicateCheckDoneMutation] = useUpdatePersonDuplicateCheckDoneMutation();

  /**
   * Remplace un stakeholder par un autre.
   * Supprime le stakeholder qui n'est plus utilisé.
   */
  const replaceEntity = useCallback(
    async ({ idToDelete, replacementId }: ReplaceEntityParams): Promise<void> => {
      await replaceEntityMutation({
        variables: {
          idToDelete,
          replacementId,
        },
      });
    },
    [replaceEntityMutation]
  );

  /**
   * Met à jour le flag duplicateCheckDone pour un stakeholder.
   * Par défaut, la valeur est positionnée à `"true"`, mais elle peut être forcée à `"false"`
   * pour relancer la vérification des doublons si nécessaire.
   */
  const updateDuplicateCheckDone = useCallback(
    async ({
      personId,
      duplicateCheckDone = "true",
    }: MarkDuplicateCheckDoneParams): Promise<void> => {
      await updatePersonDuplicateCheckDoneMutation({
        variables: {
          id: personId,
          duplicateCheckDone,
        },
      });
    },
    [updatePersonDuplicateCheckDoneMutation]
  );

  return {
    replaceEntity,
    updateDuplicateCheckDone,
  };
};
